import React, { useCallback, useState, useMemo } from 'react';
import {
	Layout,
	Row,
	Col,
	Card,
	Table,
	Button,
	Popconfirm,
	Checkbox,
	Input,
	Spin,
	Tooltip,
} from 'antd';
import moment from 'moment-timezone';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import {
	markWalkthroughCompleteForBuyer,
	markWalkthroughCompleteForSupplier,
	updateWalkthroughTaskAnswer,
	walkthroughsRestCrudThunksForBuyer,
	walkthroughsRestCrudThunksForSupplier,
} from '../../thunks/walkthroughs_thunks';
import { EmptyState } from '../empty_state/EmptyState';
import {
	getEntityById,
	getObjectValues,
	getWalkthroughScoreObject,
	nullSafeGet,
	nullSafeGetOrElse,
	onRowWithRangeSelection,
} from '../../utils/DataAccessUtils';
import {
	getPreviousTaskAnswer,
	useWalkthroughHistoryContext,
} from '../../hooks/useWalkthroughHistory';
import WalkthroughStatTiles from './WalkthroughStatTiles';
import WalkthroughScoreBreakdownCard from './WalkthroughScoreBreakdownCard';
import { getWalkthroughSectionRows } from '../../utils/WalkthroughComparisonUtils';
import WalkthroughDetailsDisplay from './WalkthroughDetailsDisplay';
import { groupBy } from 'lodash';
import TaskEditPopup from '../common/TaskEditPopup';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import WalkthroughScoreDisplay from '../walkthrough_score_display/WalkthroughScoreDisplay';
import TaskAnswerDetailsBase from '../task_answer_details/TaskAnswerDetails';
import { withUserType } from '../withUserType';
import { isWalthroughTaskAnswered, validScoreAvailable } from '../../utils/SiteSurveyUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import {
	renderTaskAnswer,
	TaskCountsDisplay,
} from '../walkthrough_task_summary_display/WalkthroughTaskSummaryDisplay';
import {
	getLastViewedWalkthroughTask,
	clearLastViewedWalkthroughTask,
} from '../../utils/WalkthroughNavigationMemory';
import { useDeepLinkScrollAndFlash } from '../../hooks/useDeepLinkScrollAndFlash';
import WalkthroughTaskAnswerCell, { LINE_CLAMP_STYLE } from './WalkthroughTaskAnswerCell';
import {
	GroupedTaskAnsweredDisplay,
	GroupedTaskTaskListScoreDisplay,
} from '../walkthrough_score_display/WalkthroughTaskListScoreDisplay';
import {
	TaskGroupProgressCircleWithPopover,
	GroupedTaskUnansweredDisplay,
} from '../walkthrough_score_display/TaskGroupProgressCircle';
import { myTimeZone } from '../../utils/DataFormatterUtils';
import {
	getAnswerCellColors,
	usesTaskItemAnswerDisplay,
	walkthroughHasScoredQuestions,
} from '../../utils/WalkthroughAnswerDisplayUtils';
import useMountEffect from '../../hooks/useMountEffect';
import useElementSize from '../../hooks/useElementSize';
import { getAnswerItems } from '../../utils/TaskItemUtils';
import WalkthroughTaskListAnswerDisplay from './WalkthroughTaskListAnswerDisplay';
import { cn } from '../../utils/ClassNamesUtils';

const { Content } = Layout;

require('./WalkthroughsDetailDetailsPage.less');

const TaskAnswerDetails: React.ComponentType<{
	record: any;
	walkthrough: any;
}> = TaskAnswerDetailsBase as any;

const sanitizeTaskAnswerTitle = (groupedAnswers) =>
	Object.keys(groupedAnswers).reduce((acc, key) => {
		acc[key] = (groupedAnswers[key] || []).map((answer, idx) =>
			answer.titleId ? answer : { ...answer, titleId: `${key}-${answer.title}-${idx}` }
		);
		return acc;
	}, {});

const EMPTY_PAST_ANSWER = <span className="footnoteregular text-character-disabled">--</span>;

// A question with no section. `groupBy` keys a missing `groupTitle` to the string 'undefined' and an
// explicitly empty one to '', so grouping on the raw field splits the untitled questions across two
// buckets that render as two separate blocks. Both mean the same thing, so they are keyed together
// here and share one untitled block, the way master gave them a single Card.
const UNGROUPED_SECTION_KEY = 'undefined';

const getSectionKey = (taskAnswer) => {
	const groupTitle = nullSafeGetOrElse('groupTitle', taskAnswer, '');
	return String(groupTitle).trim() ? groupTitle : UNGROUPED_SECTION_KEY;
};

// Titled sections keep the order they arrive in; the untitled bucket always closes the list.
const orderSectionKeys = (groupedAnswers) => {
	const titled = Object.keys(groupedAnswers).filter((key) => key !== UNGROUPED_SECTION_KEY);
	return groupedAnswers[UNGROUPED_SECTION_KEY] ? [...titled, UNGROUPED_SECTION_KEY] : titled;
};

// Every answer cell carries `walkthroughAnswerCell` — colours or not — so the stylesheet's cell
// padding applies to all of them. Colours are inline because they are per-answer, and because
// index.less forces `background-color: transparent !important` on every cell of a hovered row and
// outranks a plain inline style, the colour is also published as a custom property that the page's
// own hover rule can re-assert.
const getAnswerCellProps = (colors) => {
	if (!colors) return { className: 'walkthroughAnswerCell' };
	const style: any = {
		'--answer-cell-bg': colors.background,
		backgroundColor: colors.background,
		color: colors.text,
	};
	return { className: 'walkthroughAnswerCell', style };
};

// `onToggleUnansweredOnly` is what makes the underlined "Unanswered: N" chip a control rather than a
// label — without it GroupedTaskUnansweredDisplay still renders underlined text that looks clickable
// and does nothing. The past-scores columns render this cell read-only, so the prop is optional.
const SectionSummaryCell = ({
	groupAnswers,
	scoreObject,
	walkthrough,
	onToggleUnansweredOnly,
	unansweredOnly,
}: any) => {
	const hasScore = validScoreAvailable(scoreObject);
	return (
		<div className="flex flex-col items-center gap-1">
			{hasScore ? (
				<>
					<GroupedTaskUnansweredDisplay
						groupedAnswers={groupAnswers}
						onClick={onToggleUnansweredOnly}
						active={unansweredOnly}
					/>
					{groupAnswers.some(isWalthroughTaskAnswered) ? (
						<WalkthroughScoreDisplay scoreObject={scoreObject} walkthrough={walkthrough} />
					) : null}
				</>
			) : (
				<GroupedTaskAnsweredDisplay groupedAnswers={groupAnswers} answered />
			)}
			<GroupedTaskTaskListScoreDisplay groupedAnswers={groupAnswers} />
			{unansweredOnly ? (
				<Button type="link" size="small" className="p-0" onClick={onToggleUnansweredOnly}>
					Show all
				</Button>
			) : null}
		</div>
	);
};

const WalkthroughsDetailDetailsPage = ({
	getWalkthrough,
	updateTaskAnswer,
	markWalkthroughComplete,
	walkthroughsFetching,
	walkthrough,
	match,
	location,
	history,
	userType,
}) => {
	const walkthroughId = match.params.id;
	const [loading, setLoading] = useState(true);
	const [expandedRowKeys, setExpandedRowKeys] = useState(new Set());
	const [showMarkThisComplete, setShowMarkThisComplete] = useState(true);
	const [showEditTaskAnswer, setShowEditTaskAnswer] = useState(false);
	const [currentTaskAnswer, setCurrentTaskAnswer] = useState({});
	const [showPastScores, setShowPastScores] = useState(false);
	const [taskSearchText, setTaskSearchText] = useState('');
	// Sections currently showing only their unanswered questions, keyed the way flatTableData keys
	// them. Per-section rather than page-wide, so isolating one long section leaves the others intact.
	const [unansweredOnlyGroups, setUnansweredOnlyGroups] = useState<Set<string>>(new Set());
	const [expandedRowFocusedRecord, setExpandedRowFocusedRecord] = useState<Map<string, any>>(
		new Map()
	);
	const [sidebarMeasureRef, { height: sidebarHeight }] = useElementSize();
	const CARD_MIN_HEIGHT_PX = 450;
	const cardMaxHeight = Math.max(CARD_MIN_HEIGHT_PX, sidebarHeight);

	useMountEffect(() => {
		getWalkthrough(walkthroughId).then(() => setLoading(false));
	});

	const isUnscored = useMemo(() => !walkthroughHasScoredQuestions(walkthrough), [walkthrough]);

	// Expand + scroll to the task from ?taskTitleId=, or to the last task acted on before the Notes tab.
	const highlightedTaskTitleId = useDeepLinkScrollAndFlash({
		active: !loading && !!walkthrough,
		resolveTargetKey: () => {
			const searchParams = new URLSearchParams(nullSafeGetOrElse('search', location, ''));
			const paramTaskTitleId = searchParams.get('taskTitleId');
			return paramTaskTitleId || getLastViewedWalkthroughTask(walkthroughId);
		},
		getElement: (taskTitleId) =>
			document.querySelector(`[data-row-key="${CSS.escape(String(taskTitleId))}"]`),
		onActivate: (taskTitleId) => {
			const searchParams = new URLSearchParams(nullSafeGetOrElse('search', location, ''));
			// Consume the param so a later re-fetch doesn't re-trigger the scroll/flash.
			if (searchParams.get('taskTitleId')) {
				searchParams.delete('taskTitleId');
				const nextSearch = searchParams.toString();
				history.replace(`${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`);
			}
			// Same for the remembered-task fallback, or a stale one scrolls to the wrong row.
			clearLastViewedWalkthroughTask(walkthroughId);
			setExpandedRowKeys((prev) => new Set(prev).add(taskTitleId));
		},
		deps: [loading, walkthrough, location, walkthroughId, history],
	});

	const toggleExpandByTitle = (title) => {
		expandedRowKeys.has(title) ? expandedRowKeys.delete(title) : expandedRowKeys.add(title);

		setExpandedRowKeys(new Set(expandedRowKeys));
	};

	const onExpandIconClick = (title) => {
		toggleExpandByTitle(title);
	};

	const onExpand = (expanded, record) => {
		toggleExpandByTitle(record.titleId);
	};

	// The thunk re-reads the walkthrough after the PATCH lands, so no refetch is needed here.
	const completeWalkthrough = useCallback(
		() => markWalkthroughComplete(walkthrough.id),
		[markWalkthroughComplete, walkthrough]
	);

	const handleEdit = (taskAnswer) => {
		setCurrentTaskAnswer(taskAnswer);
		setShowEditTaskAnswer(true);
	};

	const hideTaskAnswerPopup = () => {
		setCurrentTaskAnswer({});
		setShowEditTaskAnswer(false);
	};

	const isWalkthroughComplete = walkthrough.status && walkthrough.status === 'complete';

	// Provided by WalkthroughsDetailPage so this route and the Export PDF gate share one fetch.
	const {
		history: walkthroughHistory,
		loading: historyLoading,
		error: historyError,
	} = useWalkthroughHistoryContext();
	const dataLoading = loading || historyLoading;

	const sectionRows = useMemo(
		() => getWalkthroughSectionRows(walkthrough, walkthroughHistory),
		[walkthrough, walkthroughHistory]
	);

	const hasSections = sectionRows.length > 1;

	const dismissMarkComplete = () => setShowMarkThisComplete(false);

	const previousWalkthroughs = nullSafeGetOrElse('previousWalkthroughs', walkthroughHistory, []);

	const pastScoreColumnLabels = previousWalkthroughs.map((previous) => {
		const timeZoneId = nullSafeGet('location.timeZoneId', walkthrough);
		const completedAt = nullSafeGet('completedAt', previous);
		return completedAt
			? moment(completedAt)
					.tz(timeZoneId || myTimeZone())
					.format('MMM D')
			: '--';
	});

	const canShowPastScores = previousWalkthroughs.length > 0;
	const pastScoresTooltip = canShowPastScores
		? ''
		: historyLoading
		? 'Loading historical records...'
		: historyError
		? 'Could not load historical records'
		: 'No historical records found';

	const toggleShowPastScores = (event) => setShowPastScores(event.target.checked);
	const onSearchChange = (event) => setTaskSearchText(event.target.value);

	const toggleUnansweredOnly = useCallback((gtaKey) => {
		setUnansweredOnlyGroups((current) => {
			const next = new Set(current);
			if (next.has(gtaKey)) {
				next.delete(gtaKey);
			} else {
				next.add(gtaKey);
			}
			return next;
		});
	}, []);

	const matchesTaskSearch = useCallback(
		(taskAnswer) => {
			const needle = taskSearchText.trim().toLowerCase();
			if (!needle) return true;
			const haystack = `${nullSafeGetOrElse('title', taskAnswer, '')} ${nullSafeGetOrElse(
				'description',
				taskAnswer,
				''
			)}`.toLowerCase();
			return haystack.indexOf(needle) !== -1;
		},
		[taskSearchText]
	);

	const expandRowRender = (record) => {
		const focusedRecord = showPastScores && expandedRowFocusedRecord.get(record.titleId);
		const detailRecord = focusedRecord ? { ...focusedRecord, titleId: record.titleId } : record;
		return <TaskAnswerDetails record={detailRecord} walkthrough={walkthrough} />;
	};

	const expandAll = () => {
		const titleIds = getObjectValues(groupedFilteredTaskAnswers)
			.flatMap((record) => record)
			.map((record) => record.titleId);
		setExpandedRowKeys(new Set(titleIds));
	};
	const collapseAll = () => {
		setExpandedRowKeys(new Set());
	};

	const walkthroughColumns = [
		{
			title: 'Title',
			dataIndex: 'task',
			width: 360,
			render: (text, record) => {
				if (record._type === 'spacer') return null;
				if (record._type === 'section') {
					return (
						<div className="flex items-center gap-2">
							<TaskGroupProgressCircleWithPopover groupedAnswers={record._groupAnswers} size={20} />
							<span className="bodymedium text-character-primary">{record._gtaKey}</span>
						</div>
					);
				}
				// An expanded row is being read rather than scanned, so it shows its title and description in
				// full, however many lines they take. Collapsed rows keep the clamp and the truncation so
				// the table holds its row rhythm — and keep the tooltips, which only exist because the
				// text is cut off there.
				const isExpanded = expandedRowKeys.has(record.titleId);
				const taskTitle = nullSafeGet('title', record);
				const taskDescription = nullSafeGet('description', record);
				return (
					<div className="cursor-pointer overflow-hidden">
						<Tooltip title={isExpanded ? '' : taskTitle}>
							<div
								className="bodyregular text-character-primary"
								style={isExpanded ? undefined : LINE_CLAMP_STYLE}
							>
								{taskTitle}
							</div>
						</Tooltip>
						{taskDescription && (
							<Tooltip title={isExpanded ? '' : taskDescription}>
								<div
									className="footnoteregular text-character-secondary"
									style={isExpanded ? undefined : LINE_CLAMP_STYLE}
								>
									{taskDescription}
								</div>
							</Tooltip>
						)}
					</div>
				);
			},
		},
		...(!showPastScores
			? [
					{
						title: 'Linked Items',
						dataIndex: 'task',
						width: 140,
						align: 'center' as const,
						render: (text, record) => {
							if (record._type === 'section' || record._type === 'spacer') return null;
							return (
								<div className="flex cursor-pointer flex-row items-center justify-center">
									<TaskCountsDisplay record={record} />
								</div>
							);
						},
					},
			  ]
			: []),
		{
			title: 'Score / Response',
			dataIndex: 'task',
			width: 240,
			align: 'center' as const,
			onCell: (record) => {
				if (record._type === 'spacer') return {};
				if (record._type === 'section') return { style: { paddingRight: 16 } };
				const focusedRecord = expandedRowFocusedRecord.get(record.titleId);
				const isExpanded = expandedRowKeys.has(record.titleId) && (!focusedRecord || focusedRecord === record);
				return getAnswerCellProps(getAnswerCellColors(record, undefined, isExpanded));
			},
			render: (text, record) => {
				if (record._type === 'spacer') return null;
				if (record._type === 'section') {
					return (
						<SectionSummaryCell
							groupAnswers={nullSafeGetOrElse('_groupAnswers', record, [])}
							scoreObject={record._scoreObject}
							walkthrough={walkthrough}
							unansweredOnly={record._unansweredOnly}
							onToggleUnansweredOnly={() => toggleUnansweredOnly(record._sectionKey)}
						/>
					);
				}
				return (
					<div className="flex min-w-0 cursor-pointer flex-col items-center justify-center overflow-hidden">
						<div className="flex w-full min-w-0 flex-row items-center justify-center overflow-hidden">
							{/* Switched on question type, not an `||` fallback — renderTaskAnswer is non-null for
							    multiple choice too. */}
							{usesTaskItemAnswerDisplay(record) ? (
								renderTaskAnswer(record, walkthrough, !isWalkthroughComplete)
							) : (
								<WalkthroughTaskAnswerCell
									taskAnswer={record}
									expanded={expandedRowKeys.has(record.titleId)}
								/>
							)}
						</div>
						{showPastScores && (
							<div
								className="mt-2 cursor-pointer"
								onClick={(e) => {
									e.stopPropagation();
									const isAlreadyOpen =
										expandedRowKeys.has(record.titleId) &&
										expandedRowFocusedRecord.get(record.titleId) === record;
									if (isAlreadyOpen) {
										setExpandedRowKeys((prev) => new Set([...prev].filter((k) => k !== record.titleId)));
										setExpandedRowFocusedRecord((prev) => new Map([...prev].filter(([k]) => k !== record.titleId)));
									} else {
										setExpandedRowFocusedRecord((prev) => new Map(prev).set(record.titleId, record));
										setExpandedRowKeys((prev) => new Set(prev).add(record.titleId));
									}
								}}
							>
								<TaskCountsDisplay record={record} />
							</div>
						)}
					</div>
				);
			},
		},
		...(showPastScores
			? previousWalkthroughs.map((previous, previousIndex) => ({
					title: pastScoreColumnLabels[previousIndex],
					dataIndex: 'task',
					width: 140,
					align: 'center' as const,
					onCell: (record) => {
						if (record._type === 'spacer') return {};
						if (record._type === 'section') return { style: { paddingRight: 16 } };
						const pastAnswer = getPreviousTaskAnswer(walkthroughHistory, previous, record);
						const isExpanded = Boolean(
							pastAnswer &&
								expandedRowKeys.has(record.titleId) &&
								expandedRowFocusedRecord.get(record.titleId) === pastAnswer
						);
						return getAnswerCellProps(pastAnswer ? getAnswerCellColors(record, pastAnswer, isExpanded) : null);
					},
					render: (text, record) => {
						if (record._type === 'spacer') return null;
						if (record._type === 'section') {
							// The section's answers as they stood in that walkthrough, so the same summary the
							// current column shows can be built for it.
							const pastGroupAnswers = nullSafeGetOrElse('_groupAnswers', record, [])
								.map((taskAnswer) =>
									getPreviousTaskAnswer(walkthroughHistory, previous, taskAnswer)
								)
								.filter(Boolean);
							return (
								<SectionSummaryCell
									groupAnswers={pastGroupAnswers}
									scoreObject={nullSafeGetOrElse('bySection', previous, {})[record._gtaKey]}
									walkthrough={walkthrough}
								/>
							);
						}
						const pastAnswer = getPreviousTaskAnswer(walkthroughHistory, previous, record);
						let answerContent;
						if (!pastAnswer) {
							answerContent = EMPTY_PAST_ANSWER;
						} else if (usesTaskItemAnswerDisplay(record)) {
							answerContent =
								getAnswerItems(pastAnswer).length > 0 ? (
									<WalkthroughTaskListAnswerDisplay
										taskAnswer={pastAnswer}
										timeZoneId={nullSafeGet('location.timeZoneId', walkthrough)}
									/>
								) : (
									EMPTY_PAST_ANSWER
								);
						} else {
							answerContent = (
								<WalkthroughTaskAnswerCell
									taskAnswer={record}
									answerOverride={pastAnswer}
									compact={true}
									expanded={expandedRowKeys.has(record.titleId)}
								/>
							);
						}
						return (
							<div className="flex min-w-0 flex-col items-center justify-center overflow-hidden">
								{answerContent}
								{pastAnswer && (
									<div
										className="mt-2 cursor-pointer"
										onClick={(e) => {
											e.stopPropagation();
											const isAlreadyOpen =
												expandedRowKeys.has(record.titleId) &&
												expandedRowFocusedRecord.get(record.titleId) === pastAnswer;
											if (isAlreadyOpen) {
												setExpandedRowKeys((prev) => new Set([...prev].filter((k) => k !== record.titleId)));
												setExpandedRowFocusedRecord((prev) => {
													const next = new Map(prev);
													next.delete(record.titleId);
													return next;
												});
											} else {
												setExpandedRowFocusedRecord((prev) => new Map(prev).set(record.titleId, pastAnswer));
												setExpandedRowKeys((prev) => new Set(prev).add(record.titleId));
											}
										}}
									>
										<TaskCountsDisplay record={pastAnswer} />
									</div>
								)}
							</div>
						);
					},
			  }))
			: []),
		{
			title: '',
			dataIndex: 'task',
			width: isWalkthroughComplete ? 52 : 120,
			align: 'right' as const,
			render: (text, record) => {
				if (record._type === 'section' || record._type === 'spacer') return null;
				return (
					<div className="analytics-pdf-hide-in-print flex cursor-pointer items-center justify-end gap-x-2">
						{!isWalkthroughComplete && (
							<Button
								key="new"
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									handleEdit(record);
								}}
							>
								Edit
							</Button>
						)}
						<span className="text-gray-400">
							{expandedRowKeys.has(record.titleId) ? <DownOutlined /> : <RightOutlined />}
						</span>
					</div>
				);
			},
		},
	];

	const updateAnswer = (answer) => {
		return new Promise((resolve, reject) => {
			updateTaskAnswer(walkthrough.id, answer)
				.then((res) => {
					setShowEditTaskAnswer(false);
					resolve(res);
				})
				.catch(reject);
		});
	};
	const taskAnswers =
		walkthrough.taskAnswers && walkthrough.taskAnswers.sort((a, b) => a.orderingId - b.orderingId);

	const canBeMarkedComplete =
		walkthrough.status &&
		walkthrough.status !== 'complete' &&
		nullSafeGetOrElse('taskAnswers', walkthrough, []).some(isWalthroughTaskAnswered);

	const groupedFilteredTaskAnswers = useMemo(
		() => taskAnswers && sanitizeTaskAnswerTitle(groupBy(taskAnswers, getSectionKey)),
		[taskAnswers]
	);

	const flatTableData = useMemo(() => {
		if (!groupedFilteredTaskAnswers) return [];
		return orderSectionKeys(groupedFilteredTaskAnswers).flatMap((gtaKey) => {
			const groupAnswers = groupedFilteredTaskAnswers[gtaKey];
			// Both filters apply: the search narrows by text, the per-section toggle by answered state.
			const unansweredOnly = unansweredOnlyGroups.has(gtaKey);
			const visibleAnswers = groupAnswers
				.filter(matchesTaskSearch)
				.filter((taskAnswer) => !unansweredOnly || !isWalthroughTaskAnswered(taskAnswer));
			// A section whose questions are all filtered out drops away entirely — except while its own
			// toggle is on, where the header has to stay so there is something left to switch back off.
			if (visibleAnswers.length === 0 && !unansweredOnly) return [];
			// The untitled bucket gets a section block of its own, the same as master gave it its own Card
			// — just with no title on it. `_gtaKey` is blanked so the header renders bare, and because it
			// also keys the per-section score lookups, which have nothing to find for it.
			const isUngrouped = gtaKey === UNGROUPED_SECTION_KEY;
			const scoreObject = getWalkthroughScoreObject(walkthrough, gtaKey);
			const sectionRow = {
				_type: 'section',
				_gtaKey: isUngrouped ? '' : gtaKey,
				_sectionKey: gtaKey,
				_unansweredOnly: unansweredOnly,
				_scoreObject: scoreObject,
				_groupAnswers: groupAnswers,
				titleId: `__section__${gtaKey}`,
			};
			return [
				{ _type: 'spacer', titleId: `__spacer__${gtaKey}` },
				sectionRow,
				// `_inSection` is what earns a row the nesting inset, so only a titled section's rows get
				// it. The untitled bucket has no title to nest under, so its rows stay flush left even
				// though it still gets a block of its own.
				...visibleAnswers.map((answer) => ({ ...answer, _inSection: !isUngrouped })),
			];
		});
	}, [groupedFilteredTaskAnswers, walkthrough, matchesTaskSearch, unansweredOnlyGroups]);

	return walkthroughsFetching && !nullSafeGet('id', walkthrough) ? (
		<PageLoadingPlaceholder />
	) : (
		<Content className="walkthroughsDetailDetailsPage">
			<div className="analytics-pdf-only p-4 pb-2" style={{ display: 'none' }}>
				<div className="h3medium text-character-primary">{nullSafeGet('title', walkthrough)}</div>
				{nullSafeGet('location.name', walkthrough) ? (
					<div className="bodyregular text-character-secondary">
						{nullSafeGet('location.name', walkthrough)}
					</div>
				) : null}
			</div>
			<Row className="p-4 pt-0" gutter={[12, 12]} align="stretch">
				{hasSections ? (
					<>
						<Col xs={24} xl={17} className="flex flex-col">
							<div className="flex h-full flex-col gap-3">
								{showMarkThisComplete && canBeMarkedComplete ? (
									<Card bodyStyle={{ padding: '16px 24px' }}>
										<div className="flex flex-row flex-wrap items-center justify-between gap-3">
											<span className="h5medium text-character-primary">
												Mark this walkthrough as complete?
											</span>
											<div className="flex flex-row items-center gap-3">
												<Button key="reminder" onClick={dismissMarkComplete}>
													Not yet
												</Button>
												<Popconfirm
													title="Are you sure you want to mark this walkthrough complete?"
													okText="Yes"
													cancelText="No"
													onConfirm={completeWalkthrough}
												>
													<Button type="primary" key={3}>
														Yes, mark complete
													</Button>
												</Popconfirm>
											</div>
										</div>
									</Card>
								) : null}
								<WalkthroughStatTiles
									walkthrough={walkthrough}
									history={walkthroughHistory}
									isUnscored={isUnscored}
								/>
								{dataLoading ? (
									<Card className="flex min-h-0 flex-1 items-center justify-center">
										<Spin />
									</Card>
								) : (
									<WalkthroughScoreBreakdownCard
										sectionRows={sectionRows}
										isUnscored={isUnscored}
										className="min-h-0 flex-1"
										maxHeight={cardMaxHeight}
									/>
								)}
							</div>
						</Col>
						<Col xs={24} xl={7}>
							<div ref={sidebarMeasureRef} className="h-full bg-white">
								<Card title="Walkthrough Details">
									<WalkthroughDetailsDisplay walkthrough={walkthrough} />
								</Card>
							</div>
						</Col>
					</>
				) : (
					<Col span={24}>
						<div className="flex flex-col gap-3">
							{showMarkThisComplete && canBeMarkedComplete ? (
								<Card bodyStyle={{ padding: '16px 24px' }}>
									<div className="flex flex-row flex-wrap items-center justify-between gap-3">
										<span className="h5medium text-character-primary">
											Mark this walkthrough as complete?
										</span>
										<div className="flex flex-row items-center gap-3">
											<Button key="reminder" onClick={dismissMarkComplete}>
												Not yet
											</Button>
											<Popconfirm
												title="Are you sure you want to mark this walkthrough complete?"
												okText="Yes"
												cancelText="No"
												onConfirm={completeWalkthrough}
											>
												<Button type="primary" key={3}>
													Yes, mark complete
												</Button>
											</Popconfirm>
										</div>
									</div>
								</Card>
							) : null}
							<WalkthroughStatTiles
								walkthrough={walkthrough}
								history={walkthroughHistory}
								isUnscored={isUnscored}
							/>
							<Card title="Walkthrough Details">
								<div className="materialCard__body p-0">
									<div className="materialCard__supportingText">
										<WalkthroughDetailsDisplay
											walkthrough={walkthrough}
											horizontal={true}
											keyWidth={120}
										/>
									</div>
								</div>
							</Card>
						</div>
					</Col>
				)}
				<Col span={24}>
					{walkthrough.taskAnswers && walkthrough.taskAnswers.length > 0 ? (
						<div className="analytics-pdf-hide-in-print flex flex-row flex-wrap items-center justify-between gap-3">
							<div className="flex flex-row items-center gap-3">
								<Input.Search
									allowClear
									placeholder="Search by title"
									value={taskSearchText}
									onChange={onSearchChange}
									style={{ width: 240 }}
								/>
							</div>
							<div className="flex flex-row items-center gap-1">
								<Tooltip title={pastScoresTooltip}>
									<span>
										<Checkbox
											checked={showPastScores && canShowPastScores}
											disabled={!canShowPastScores}
											onChange={toggleShowPastScores}
										>
											Compare past 3
										</Checkbox>
									</span>
								</Tooltip>
								<Button type={'text'} onClick={expandAll}>
									Expand All
								</Button>
								<Button type={'text'} onClick={collapseAll}>
									Collapse All
								</Button>
							</div>
						</div>
					) : null}
				</Col>
				<Col span={24}>
					{walkthrough.taskAnswers && walkthrough.taskAnswers.length > 0 ? (
						<Table
							tableLayout="fixed"
							showHeader={true}
							pagination={false}
							loading={loading}
							className={hasSections ? 'walkthroughTable--sectioned' : undefined}
							columns={walkthroughColumns}
							rowKey={(el) => el.titleId}
							rowClassName={(record) => {
								if (record._type === 'section') return 'walkthroughSectionRow';
								if (record._type === 'spacer') return 'walkthroughSpacerRow';
								const nested = record._inSection ? ' walkthroughTaskRow--sectioned' : '';
								if (record.titleId === highlightedTaskTitleId)
									return `walkthroughTaskRow walkthroughTaskRow--highlighted${nested}`;
								return `walkthroughTaskRow${nested}`;
							}}
							dataSource={flatTableData}
							expandable={{
								expandIconColumnIndex: -1,
								expandedRowRender: expandRowRender,
								rowExpandable: (record) => record._type !== 'section' && record._type !== 'spacer',
								expandedRowClassName: (record) =>
									record._inSection ? 'walkthroughExpandedRow--sectioned' : '',
								expandedRowKeys: expandedRowKeys as any,
								onExpand: onExpand,
							}}
							onRow={onRowWithRangeSelection((record, i) => ({
								onClick: (event) => {
									if (record._type === 'section' || record._type === 'spacer') return;
									// A direct row click resets to the full detail view — clears any
									// focused linked-items source set by a TaskCountsDisplay click.
									if (expandedRowFocusedRecord.has(record.titleId)) {
										setExpandedRowFocusedRecord((prev) => new Map([...prev].filter(([k]) => k !== record.titleId)));
									}
									onExpandIconClick(record.titleId);
								},
							}))}
						/>
					) : (
						<EmptyState
							height={120}
							headline={`Walkthrough incomplete`}
							body={
								<div className="max-w-[488px] text-center">
									Walkthrough has not been conducted yet
								</div>
							}
						/>
					)}
				</Col>
			</Row>
			{showEditTaskAnswer && (
				<TaskEditPopup
					taskAnswer={currentTaskAnswer}
					onCancel={hideTaskAnswerPopup}
					onSave={updateAnswer}
					walkthrough={walkthrough}
				/>
			)}
		</Content>
	);
};

const mapStateToProps = (state, ownProps) => ({
	walkthroughsFetching: state.walkthroughs.fetching,
	// Keyed by the route id — the slice's shared `detail` slot can hold a previously fetched walkthrough.
	walkthrough: getEntityById(nullSafeGet('match.params.id', ownProps), state.walkthroughs),
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	getWalkthrough: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? walkthroughsRestCrudThunksForSupplier.readOne(id)
				: walkthroughsRestCrudThunksForBuyer.readOne(id)
		),
	updateTaskAnswer: (id, entity) =>
		dispatch(updateWalkthroughTaskAnswer(ownProps.userType)(id, entity)),
	markWalkthroughComplete: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? markWalkthroughCompleteForSupplier(id)
				: markWalkthroughCompleteForBuyer(id)
		),
});

export default withRouter(
	withUserType(
		connect(mapStateToProps, mapDispatchToProps)(WalkthroughsDetailDetailsPage as any) as any
	)
);
